import React, { useCallback, useState } from 'react';

import { Paper } from '@mantine/core';
import PackageSummary from 'components/SalesPortal/PackageSummary';
import PurchaseConfirmation, {
  deriveConfirmationVariant,
} from 'components/SalesPortal/PurchaseConfirmation';
import { useAuth } from 'context/auth-context';
import { useNavigate } from 'react-router-dom';
import { isFirstInstallmentFree } from 'utils/discount.helpers';
import notificationHandler from 'utils/notifications';

import { useCheckout } from '../../CheckoutContext';

// The public checkout's confirmation: the shared component fed by the enriched
// checkout response (spec Item 1). "Open My Schedule" keeps the old "See Your
// Packages" mechanics — select the target athlete profile, then land in the athlete
// web app, whose schedule is its root route.
const Confirmation = (): JSX.Element => {
  const { selectProfileAsync } = useAuth();
  const { packageData, billingPlan, appliedDiscount, checkoutResult } = useCheckout();
  const navigate = useNavigate();
  const [isOpeningSchedule, setIsOpeningSchedule] = useState(false);
  const { infoNotification } = notificationHandler();

  const handleOpenSchedule = useCallback(async (): Promise<void> => {
    if (isOpeningSchedule || !checkoutResult) return;

    setIsOpeningSchedule(true);
    try {
      await selectProfileAsync(checkoutResult.targetAthleteProfileId);
      navigate('/');
    } catch (error) {
      infoNotification('We were unable to open your schedule', 'Please try again later');
    } finally {
      setIsOpeningSchedule(false);
    }
  }, [isOpeningSchedule, checkoutResult, selectProfileAsync, navigate, infoNotification]);

  const flags = checkoutResult ?? {
    reservationOutcome: 'none' as const,
    waiverPending: false,
    newAccount: false,
  };

  return (
    <PurchaseConfirmation
      variant={deriveConfirmationVariant(flags)}
      waiverPending={flags.waiverPending}
      noPaymentTaken={isFirstInstallmentFree(billingPlan, appliedDiscount)}
      kiloClass={checkoutResult?.kiloClass}
      onOpenSchedule={handleOpenSchedule}
      isOpeningSchedule={isOpeningSchedule}
    >
      <Paper withBorder p='lg' shadow='md' w='100%' maw={560} data-cy='confirmationPackageSummary'>
        <PackageSummary pckg={packageData} />
      </Paper>
    </PurchaseConfirmation>
  );
};

export default Confirmation;
